import { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import { BoxForm, Select, Switch, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { TooltipProps } from 'components/Tooltip'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { DomainSlice, useGetDomainsForSelect } from '../../hooks/useGetDomainsForSelect'
import { CommonSchema } from '../../schemas/commonSchema'
import { CommonFormProps } from '../../types'
import { isRefreshControlledByYb } from '../../utils'
import { usePubAdsServiceRefresh } from './usePubAdsServiceRefresh'

export const BasicAttributesSection = ({ variant }: CommonFormProps): React.JSX.Element => {
  const { workspaceId } = useWorkspaceParam()
  const ability = useAbility()
  const { t } = useTranslation('features/page', {
    keyPrefix: 'form.general.basicAttributes',
  })
  const { control, setValue } = useFormContext<CommonSchema>()
  const { domainId } = useWatch<CommonSchema>()

  const { domains } = useGetDomainsForSelect()

  const { overriddenByYieldbird, pubAdsServiceRefresh } = usePubAdsServiceRefresh({
    domains,
    variant,
  })
  const refreshControlledByYb: boolean = isRefreshControlledByYb({
    overriddenByYieldbird,
    pubAdsServiceRefresh,
  })

  useEffect(() => {
    if (variant === 'add') {
      setValue('onlySendAdRequestsForAssignedAdUnits', refreshControlledByYb)
    }
  }, [refreshControlledByYb, variant])

  const domainIdTooltip: TooltipProps = {
    content: [t('domainId.tooltip.line1'), t('domainId.tooltip.line2')],
  }

  const isDisabledByAuth: boolean =
    variant === 'add' ? ability.cannot('create', 'Page') : ability.cannot('update', 'Page')

  return (
    <Paper>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <BoxForm>
        <Select<CommonSchema, DomainSlice>
          control={control}
          disabled={isDisabledByAuth || variant === 'edit'}
          label={t('domainId.label')}
          name='domainId'
          options={domains}
          tooltip={domainIdTooltip}
        />

        <TextField
          control={control}
          disabled={isDisabledByAuth}
          label={t('name.label')}
          name='name'
          placeholder={t('name.placeholder')}
        />

        <Switch
          control={control}
          disabled={isDisabledByAuth || !refreshControlledByYb}
          label={t('onlySendAdRequestsForAssignedAdUnits.label')}
          labelPlacement='start'
          name='onlySendAdRequestsForAssignedAdUnits'
          tooltip={{
            content: t('onlySendAdRequestsForAssignedAdUnits.tooltip.general'),
          }}
          tooltipDisabled={
            !refreshControlledByYb
              ? {
                  content: t('onlySendAdRequestsForAssignedAdUnits.tooltip.disabled.content'),
                  link: {
                    title: t('onlySendAdRequestsForAssignedAdUnits.tooltip.disabled.link'),
                    url: generatePath(ROUTES.INVENTORY.DOMAIN.EDIT, {
                      id: domainId,
                      tab: 'general',
                      workspaceId,
                    }),
                  },
                }
              : undefined
          }
        />
      </BoxForm>
    </Paper>
  )
}
